import { ApiClient } from "../ApiClient";
import { apiFailureFromResponse } from "../../utils/api/parseApiErrorMessage";

/**
 * Fetch all budgets for the authenticated user
 * @returns {Promise<Object>} API response with transformed budget data
 */
export const fetchBudgets = async () => {
  const response = await ApiClient.get("/finances/budget");

  if (response.ok) {
    const result = await response.json();

    const rawBudgets = result.budgets || [];
    const summary = result.summary || {
      total_limit: 0,
      total_spent: 0,
      percentage: 0,
    };

    const transformedBudgets = rawBudgets.map((b) => ({
      uuid: b.uuid,
      name: b.name,
      description: b.description,
      frequency: b.frequency,
      spent: parseFloat(b.amount_spent) || 0,
      limit: parseFloat(b.amount_limit) || 0,
      threshold: b.threshold,
      status: b.status,
      institutions: b.institutions,
      accounts: b.accounts,
      categories: b.categories,
    }));

    return {
      success: true,
      data: {
        budgets: transformedBudgets,
        summary: summary,
      },
      status: response.status,
    };
  }

  return apiFailureFromResponse(response, "Failed to fetch budgets");
};

/**
 * Create a new budget
 */
export const createBudget = async (budgetData) => {
  const frequencyMap = {
    Weekly: "WE",
    Monthly: "MO",
  };

  const payload = {
    name: budgetData.name,
    description: budgetData.description,
    frequency: frequencyMap[budgetData.frequency],
    amount_limit: budgetData.amount_limit,
    threshold: budgetData.threshold,
    categories: budgetData.categories,
    institution_ids: budgetData.institutions,
    account_ids: budgetData.accounts,
  };

  const response = await ApiClient.post("/finances/budget", payload);

  if (response.ok) {
    const result = await response.json();

    const newBudget = {
      uuid: result.uuid,
      name: result.name,
      description: result.description,
      frequency: result.frequency,
      spent: parseFloat(result.amount_spent) || 0,
      limit: parseFloat(result.amount_limit) || 0,
      threshold: result.threshold || "85",
      status: result.status || "Good",
      institutions: result.institutions || [],
      accounts: result.accounts || [],
      categories: result.categories || [],
    };

    return { success: true, data: newBudget, status: response.status };
  }

  return apiFailureFromResponse(
    response,
    "Failed to create budget. Please try again.",
  );
};

/**
 * Delete a budget by UUID
 */
export const deleteBudget = async (budgetUuid) => {
  const response = await ApiClient.delete(`/finances/budget/${budgetUuid}`);

  if (response.ok) {
    return { success: true, status: response.status };
  }

  return apiFailureFromResponse(
    response,
    "Failed to delete budget. Please try again.",
  );
};

/**
 * Fetch historical snapshots for a specific budget
 */
export const fetchBudgetHistory = async (budgetUuid) => {
  const response = await ApiClient.get(
    `/finances/budgets/snapshots?budget_id=${budgetUuid}`,
  );

  if (response.ok) {
    const snapshots = await response.json();
    const statusLabels = {
      SA: "Save",
      GO: "Good",
      RI: "At Risk",
      PA: "Passed",
    };

    const transformedHistory = snapshots.map((snap) => ({
      uuid: snap.uuid,
      label:
        snap.historical_date_range ||
        new Date(snap.created_at || snap.period_end).toLocaleDateString(
          "en-US",
          {
            month: "short",
            day: "numeric",
            year: "numeric", // <--- Include full year
          },
        ),
      value: parseFloat(snap.historical_amount_spent) || 0,
      status:
        statusLabels[snap.historical_status] ||
        snap.historical_status ||
        "Good",
    }));

    return {
      success: true,
      data: transformedHistory,
    };
  }

  return apiFailureFromResponse(response, "Failed to load budget history");
};

/**
 * Fetch only the processed transactions belonging to a specific budget cycle
 */
export const fetchBudgetTransactions = async (budgetUuid) => {
  const response = await ApiClient.get(
    `/finances/budget/${budgetUuid}?only_transactions=true`,
  );

  if (response.ok) {
    const data = await response.json();
    return {
      success: true,
      data: data,
    };
  }

  return apiFailureFromResponse(response, "Failed to load budget transactions");
};

/**
 * Fetch a single historical budget snapshot detail
 */
export const fetchBudgetSnapshotDetails = async (snapshotUuid) => {
  const response = await ApiClient.get(
    `/finances/budgets/snapshots/${snapshotUuid}`,
  );

  if (response.ok) {
    const data = await response.json();
    return {
      success: true,
      data: data,
    };
  }

  return apiFailureFromResponse(response, "Failed to load snapshot details");
};
